import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { useFocusTrap } from "@kundeninfo/ui";
import { ApiError } from "@kundeninfo/api-client";
import { apiMeldung } from "./vorgangsdetail.js";
import { KiKnopf } from "./meHuellen.js";
import { Chip, Optionen } from "@kundeninfo/ui/me-design";
import "./Belegvorschau.css";

/**
 * **Die BELEGVORSCHAU** (Spec „Belegvorschau", 24.09.2026): eine vollflächige
 * Überlagerung, die das EXAKTE Druckbild eines Belegs Seite für Seite zeigt — die Bilder
 * kommen aus dem PDF, das die Engine gerade erzeugt hat.
 *
 * Entscheid Dirk 23.09.2026, wörtlich: „Wir benötigen einen aussagefähigen Preview für
 * ein Angebot, damit Zeilen- und Seitenumbrüche und die allgemeine Optik bewertet werden
 * können. Wir legen großen Wert auf das Erscheinungsbild unserer Angebote." Das ist der
 * Grund für Bilder statt HTML: Eine HTML-Vorschau wäre eine ZWEITE Umbruchrechnung mit
 * eigenen Ergebnissen, und genau die Umbrüche sollen beurteilt werden.
 *
 * **Vier Entscheidungen tragen diesen Baustein:**
 *
 * **Er kennt KEINE Belegart und KEINEN Klienten.** `laden()` bringt der Aufrufer mit,
 * ebenso `titel` und `fassung`. Damit ist derselbe Baustein dreimal eingehängt (Angebot,
 * Auftragsbestätigung, Rechnung), ohne eine einzige Verzweigung — und das Blatt ist
 * genau das, was der Designer später wiederverwendet (Spec, Nicht-Ziele).
 *
 * **Er ist KEIN Modal.** Ein Modal hat einen Entwurf und zwei Ausgänge (Speichern,
 * Abbrechen); die Vorschau hat nichts zu speichern. Die Hausregel „Modal-Bearbeitung"
 * gilt für sie deshalb ausdrücklich nicht. Was sie sich leiht, sind die beiden Bausteine
 * aus `@kundeninfo/ui`: `useFocusTrap` (Fokusfalle) und, über ihn, die gemeinsame
 * Escape-EBENE. Der Ebenenstapel ist wichtig: Ein eigener Escape-Horcher träfe auch die
 * Schichten darunter, und dann schlösse eine Taste die Vorschau UND das Menü, aus dem
 * sie geöffnet wurde (Befund vom 10.09.2026, `useEscapeEbene`).
 *
 * **Er hält keinen Cache.** Beim Öffnen wird gerendert, beim Schliessen ist alles weg
 * (Spec, wörtlich). Ein zwischengespeichertes Blatt, das nicht mehr zum Beleg passt, ist
 * schlimmer als eine Wartezeit — Prioritätenkette: Funktion vor Performance.
 *
 * **VERALTEN heisst warnen, nicht neu rendern.** Ändert sich die `fassung` (der Aufrufer
 * bildet sie aus Version und, wo nötig, Status), erscheint ein Warnhinweis mit dem Stand
 * und dem Weg zum Neu-Rendern. Die BILDER bleiben stehen: Wer gerade Umbrüche
 * vergleicht, will nicht, dass ihm das Blatt unter dem Blick weggetauscht wird.
 *
 * Block: `bvor__`. Farben ausschliesslich über die ME-Tokens (seit P10); der Kopf ist sticky
 * (Hausregel), und der Pinch-Zoom des Browsers bleibt erlaubt (kein `user-scalable=no`).
 */

/** Eine Seite, wie die API sie liefert. Die Masse gehen ans `<img>`, damit beim Laden
 *  nichts springt — sie kommen aus dem PNG selbst und können deshalb nicht abweichen. */
export type BelegvorschauSeite = {
  nummer: number;
  breite: number;
  hoehe: number;
  /** Das Seitenbild als Base64 (`image/png`) — wird zu einem `data:`-URL. */
  png: string;
};

/** Der Stand einer Vorschau, wie die drei `…/vorschau`-Wege ihn antworten. */
export type BelegvorschauStand = {
  seiten: BelegvorschauSeite[];
  anzahl: number;
  /** Ob DIESE Bilder das Wort „ENTWURF" tragen — der Chip im Kopf sagt dasselbe. */
  entwurf: boolean;
  /** Die Hinweise des Prüfberichts der Engine (u. a. das nicht gelaufene Laufzeitgate). */
  hinweise: string[];
  /** ISO-Zeitpunkt des Renderlaufs — daraus wird „Stand von hh:mm". */
  standVon: string;
};

export type BelegvorschauProps = {
  /** Die Kopfzeile, z. B. „Angebot ANG-2026-0003 v2 · Vorschau". Sie ist auch der
   *  zugängliche Name der Überlagerung. */
  titel: string;
  /** Rendert (neu). Wirft bei Fehlschlag — ein `ApiError` wird hier übersetzt. */
  laden: () => Promise<BelegvorschauStand>;
  /**
   * Die aktuelle Fassung des Belegs. Ändert sie sich gegenüber der, mit der gerendert
   * wurde, gilt der Stand als VERALTET.
   *
   * Eine Zeichenkette und keine Zahl, damit der Aufrufer zusammensetzen kann, was sich
   * bei ihm ändern kann (`` `${version}` ``, an der Rechnung
   * `` `${version}:${status}` ``) — die Vorschau soll nicht wissen müssen, woraus sich
   * „hat sich geändert" bei welcher Belegart ergibt.
   */
  fassung: string;
  /** Der bestehende PDF-Download-Weg derselben Belegart. Fehlt er, fehlt der Knopf. */
  aufPdf?: (() => void | Promise<void>) | undefined;
  onSchliessen: () => void;
};

type Zoom = "breite" | "voll";

/** Was schiefgehen kann — mit der ART, damit Klasse und Rolle nie auseinanderlaufen
 *  (dasselbe Muster wie `PdfMeldung` an den Detailansichten). */
type Fehlerstand = {
  text: string;
  art: "hinweis" | "schlecht";
  felder: { field: string; message: string }[];
};

/** „Stand von hh:mm" aus dem ISO-Zeitpunkt des Renderlaufs — in der Zeitzone des
 *  Bearbeiters, denn er liest ihn. */
function uhrzeit(iso: string): string {
  const zeit = new Date(iso);
  if (Number.isNaN(zeit.getTime())) return "unbekannt";
  return zeit.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export function Belegvorschau(p: BelegvorschauProps): ReactElement {
  const rahmen = useRef<HTMLDivElement>(null);
  const seitenRumpf = useRef<HTMLDivElement>(null);
  const [stand, setStand] = useState<BelegvorschauStand | null>(null);
  /** Die Fassung, mit der der angezeigte Stand gerendert wurde. */
  const [standFassung, setStandFassung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<Fehlerstand | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [zoom, setZoom] = useState<Zoom>("breite");
  const [sichtbareSeite, setSichtbareSeite] = useState(1);

  /*
   * Die Fokusfalle — und mit ihr die Escape-Ebene dieser Überlagerung. `true`, solange
   * der Baustein da ist: Er wird vom Aufrufer bedingt gezeichnet, es gibt also keinen
   * „geschlossenen" Zustand, in dem er noch im Baum hinge.
   */
  useFocusTrap(true, rahmen, p.onSchliessen);

  /*
   * `laden` und `fassung` als Ref: Der Aufrufer gibt `laden` fast immer als
   * Inline-Funktion, also bei jedem Neuzeichnen eine neue Identität. Stünde sie in den
   * Abhängigkeiten von `rendern`, liefe der Effekt unten bei JEDER Zustandsänderung neu
   * an — und die Vorschau renderte sich in einer Schleife. Dasselbe Muster wie
   * `onCloseRef` in useFocusTrap, und aus demselben Grund: Der Baustein soll bei
   * unachtsamer Benutzung nicht stillschweigend das Falsche tun.
   */
  const ladenRef = useRef(p.laden);
  ladenRef.current = p.laden;
  const fassungRef = useRef(p.fassung);
  fassungRef.current = p.fassung;

  const rendern = useCallback(async (): Promise<void> => {
    setLaedt(true);
    setFehler(null);
    // Die Fassung wird VOR dem Aufruf festgehalten: Was gerendert wird, ist der Stand
    // von JETZT — ändert der Vorgang sich während des Renderlaufs, ist das Ergebnis
    // veraltet, und genau das soll der Hinweis danach sagen.
    const gerendertMit = fassungRef.current;
    try {
      const frisch = await ladenRef.current();
      setStand(frisch);
      setStandFassung(gerendertMit);
      /*
       * Der Blick wandert mit den neuen Blättern zurück an den Anfang — und zwar am DOM,
       * nicht nur im Zähler (Fixrunde 1): `setSichtbareSeite(1)` allein wäre eine
       * Behauptung. Blieb der Rumpf auf halber Höhe stehen, sagte der Kopf „Seite 1",
       * während Seite 4 im Bild stand, bis der erste Rollschub die Rechnung nachholte.
       * Zurück an den Anfang ist ausserdem das Erwartbare: Ein neu gerendertes Blatt sieht
       * man von vorn an.
       */
      if (seitenRumpf.current !== null) seitenRumpf.current.scrollTop = 0;
      setSichtbareSeite(1);
    } catch (e) {
      setFehler({
        // Die 501 der nicht angebundenen Engine ist ein bekannter Arbeitsstand und kein
        // Fehlschlag — als Status angesagt, neutral gezeichnet (dieselbe Unterscheidung
        // wie am „Als PDF"-Knopf). Alles andere ist ein Alarm.
        art: e instanceof ApiError && e.status === 501 ? "hinweis" : "schlecht",
        text: apiMeldung(e, "Die Vorschau konnte nicht erzeugt werden."),
        // Nennt die Engine Feldpfade, reisen sie strukturiert mit (`fields[]`) — wer sie
        // nicht liest, zeigt sie nirgends, und „konnte nicht erzeugt werden" sagt
        // niemandem, WAS fehlt.
        felder: e instanceof ApiError && e.fields !== undefined ? e.fields : [],
      });
    } finally {
      setLaedt(false);
    }
  }, []);

  /*
   * Gerendert wird EINMAL beim Öffnen. Der Riegel ist nötig, weil `rendern` zwar stabil
   * ist, der Effekt aber bei einem erneuten Einhängen (React 19 im Entwicklungsmodus
   * hängt zweimal ein) sonst zwei Renderaufträge abschickte — zwei PDF-Läufe für ein
   * Bild.
   */
  const gelaufen = useRef(false);
  useEffect(() => {
    if (gelaufen.current) return;
    gelaufen.current = true;
    void rendern();
  }, [rendern]);

  /*
   * Der Seitenzähler folgt dem Rollen (Spec). Gerechnet wird beim Rollen und nicht über
   * einen IntersectionObserver: Die Frage ist „welche Seite steht oben", und das ist eine
   * Rechnung über Abstände, keine Sichtbarkeitsfrage. Ein Observer bräuchte ausserdem
   * einen Schwellenwert, der bei einem Blatt, das höher ist als das Fenster, nie
   * erreicht wird.
   */
  const beiRollen = useCallback((): void => {
    const rumpf = seitenRumpf.current;
    if (rumpf === null) return;
    /*
     * Ohne gemessene Höhe wird NICHT gerechnet (Fixrunde 1). Ist der Rumpf noch nicht
     * eingemessen, bleiben alle Kanten bei 0 — dann passt JEDE Seite in die Bedingung
     * unten, und der Zähler spränge auf die letzte, obwohl oben die erste steht. Der
     * bisherige Wert ist in diesem Fall die bessere Auskunft. (Im Test ist das der
     * Normalfall: jsdom rechnet kein Layout und gibt überall 0.)
     */
    if (rumpf.clientHeight === 0) return;
    const blaetter = Array.from(
      rumpf.querySelectorAll<HTMLElement>("[data-seite]"));
    /*
     * Gesucht ist die LETZTE Seite, deren Oberkante noch im oberen Drittel des Rumpfes
     * (oder darüber) liegt — also die Seite, die den Kopf des Sichtfensters gerade füllt.
     * Das Drittel ist die Umschaltschwelle: Sie wechselt die Anzeige, sobald die nächste
     * Seite das obere Drittel erreicht hat, und nicht erst, wenn die vorige ganz weg ist.
     * Die Blätter stehen in Dokumentreihenfolge, die letzte Übereinstimmung ist damit die
     * tiefste passende. Vor der ersten Seite bleibt es bei 1.
     */
    let oben = 1;
    for (const blatt of blaetter) {
      const nummer = Number(blatt.dataset.seite ?? "1");
      if (blatt.offsetTop - rumpf.scrollTop <= rumpf.clientHeight / 3) oben = nummer;
    }
    setSichtbareSeite(oben);
  }, []);

  /*
   * Nachrechnen nach jedem LAYOUTWECHSEL (Fixrunde 1). Der Zähler hängt am Rollen, aber
   * zwei Dinge verschieben die Blätter, ohne dass gerollt wird: ein neuer Stand (andere
   * Seitenzahl, andere Höhen) und der Zoomwechsel (in „100 %" ist jedes Blatt so hoch wie
   * das Bild, in „Breite" so hoch wie die Spalte es zulässt). Ohne diesen Lauf stünde im
   * Kopf die Seite von vorher, bis jemand rollt.
   */
  useEffect(() => { beiRollen(); }, [zoom, stand, beiRollen]);

  const veraltet = stand !== null && standFassung !== null && standFassung !== p.fassung;

  return (
    <div className="bvor__hintergrund" role="presentation">
      <div className="bvor" role="dialog" aria-modal="true" aria-label={p.titel}
        ref={rahmen} tabIndex={-1}>
        {/* Der KOPF ist sticky (Hausregel „Feststehendes Top-Menü", hier auf die
            Überlagerung angewandt) und trägt alles, was man beim Blättern braucht. */}
        <div className="bvor__kopf">
          {/* Schlechtestes oben: Fehler VOR allem anderen — vor Titel, Zähler, Bildern. */}
          {fehler !== null && (
            <div className={fehler.art === "schlecht"
              ? "bvor__meldung" : "bvor__meldung bvor__meldung--hinweis"}
              role={fehler.art === "schlecht" ? "alert" : "status"}>
              {fehler.text}
              {fehler.felder.length > 0 && (
                <ul className="bvor__felder">
                  {/* Die Laufnummer im Schlüssel, weil die Engine denselben Feldpfad
                      zweimal nennen darf (zwei Befunde an einem Feld) und zwei Hinweise
                      wortgleich sein können — doppelte Schlüssel sind für React ein
                      Fehler, und sie verwirft dann stillschweigend den zweiten Eintrag. */}
                  {fehler.felder.map((f, i) => (
                    <li key={`${i}:${f.field}`}>
                      <span className="bvor__feldname">{f.field}</span>
                      <span className="bvor__feldgrund">{f.message}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {/* Der Veralten-Hinweis steht als WARNUNG vor dem Zähler — er ist die
              wichtigere Aussage: Was man sieht, gilt nicht mehr ganz. */}
          {veraltet && stand !== null && (
            <p className="bvor__veraltet" role="alert">
              {`Stand von ${uhrzeit(stand.standVon)} — der Beleg wurde seitdem geändert. `}
              {/* Der Weg heisst im Hinweis WOERTLICH so wie der Knopf daneben
                  („Neu rendern“): Ein Hinweis, der die Handlung anders benennt als die
                  Schaltfläche, lässt suchen. */}
              Bitte „Neu rendern“.
            </p>
          )}
          <div className="bvor__kopfzeile">
            <h2 className="bvor__titel">{p.titel}</h2>
            {stand?.entwurf === true && <Chip className="bvor__chip" statusArt="warnung">Entwurf</Chip>}
            {stand !== null && (
              <span className="bvor__zaehler" aria-live="polite">
                {`Seite ${sichtbareSeite} / ${stand.anzahl}`}
              </span>
            )}
            <div className="bvor__werkzeuge">
              <Optionen beschriftung="Zoom" darstellung="segmente" richtung="waagerecht"
                wert={zoom}
                onGeaendert={(d) => { if (d.wert === "breite" || d.wert === "voll") setZoom(d.wert); }}>
                <option value="breite">Breite</option>
                <option value="voll">100 %</option>
              </Optionen>
              <KiKnopf className="bvor__knopf" deaktiviert={laedt}
                onClick={() => { void rendern(); }}>
                Neu rendern
              </KiKnopf>
              {p.aufPdf !== undefined && (
                <KiKnopf className="bvor__knopf"
                  /* `Promise.resolve(…).catch`: `aufPdf` darf synchron ODER asynchron
                     sein (der Vertrag lässt beides). Bei der asynchronen Fassung liefe ein
                     Fehlschlag als unbehandelte Ablehnung auf — in der Konsole, nicht in
                     der Maske. Der Download meldet sich an der Detailansicht selbst
                     (PdfMeldung), sie ist der richtige Ort dafür; hier wird die Ablehnung
                     nur ordentlich geschluckt. */
                  onClick={() => { void Promise.resolve(p.aufPdf?.()).catch(() => {}); }}>
                  PDF herunterladen
                </KiKnopf>
              )}
              <KiKnopf className="bvor__knopf"
                onClick={p.onSchliessen}>
                Schliessen
              </KiKnopf>
            </div>
          </div>
          {/* Die Hinweise des Prüfberichts — leise, aber da: Dass das
              veraPDF-Laufzeitgate für eine Vorschau nicht läuft, soll niemand raten. */}
          {stand !== null && stand.hinweise.length > 0 && (
            <ul className="bvor__hinweise">
              {stand.hinweise.map((h, i) => <li key={`${i}:${h}`}>{h}</li>)}
            </ul>
          )}
        </div>

        {/*
          * `aria-busy` steht am RUMPF und nicht am Skelett (Fixrunde 1): Es sagt „dieser
          * Bereich wird gerade aufgebaut", und der Bereich ist der Rumpf — das Skelett ist
          * nur seine Füllung und verschwindet mitsamt seinem Attribut, sobald die Bilder
          * da sind. Es gilt auch beim ZWEITEN Renderlauf über stehenden Bildern, bei dem
          * es gar kein Skelett gibt.
          */}
        <div className={`bvor__seiten bvor__seiten--${zoom}`} data-testid="bvor-seiten"
          ref={seitenRumpf} onScroll={beiRollen} aria-busy={laedt}>
          {/* KEIN role="status" am Skelett: Die eine Rolle „status“ dieser Überlagerung
              gehört der Meldung im Kopf. Trug das Skelett sie auch, fand eine Suche nach
              dieser Rolle während des Ladens das Skelett statt der Meldung, die gleich
              danach erscheint (Befund K5).

              `aria-live` bleibt stehen, aber ehrlich benannt (Fixrunde 1): Eine
              Live-Region, die MIT ihrem Inhalt eingehängt wird, sagen die Vorleser in der
              Regel NICHT an — sie melden Änderungen an einer schon vorhandenen Region. Der
              Ladehinweis ist damit faktisch stumm, und das ist hier vertretbar: Die
              Ansage, auf die es ankommt, ist das ERGEBNIS (Meldung im Kopf, Rolle „alert"
              bzw. „status"), und `aria-busy` am Rumpf sagt in der Zwischenzeit, dass dort
              gearbeitet wird. */}
          {laedt && stand === null && (
            <div className="bvor__skelett" data-testid="bvor-skelett" aria-live="polite">
              {/* Ein Blatt in A4-Verhältnis als Platzhalter — die Maske soll beim
                  Eintreffen der Bilder nicht die Höhe wechseln. */}
              <div className="bvor__blatt bvor__blatt--skelett" />
              <p className="bvor__skeletttext">Der Beleg wird gerendert …</p>
            </div>
          )}
          {stand?.seiten.map((seite) => (
            <figure className="bvor__blattrahmen" key={seite.nummer}
              data-seite={seite.nummer}>
              <img className="bvor__blatt" alt={`Seite ${seite.nummer}`}
                src={`data:image/png;base64,${seite.png}`}
                width={seite.breite} height={seite.hoehe} />
              <figcaption className="bvor__blattnummer">
                {`Seite ${seite.nummer}`}
              </figcaption>
            </figure>
          ))}
          {/* Ein zweites Rendern über einem vorhandenen Stand: Die Bilder bleiben
              stehen, der Kopf sagt, dass gearbeitet wird. */}
          {laedt && stand !== null && (
            <p className="bvor__laedt" role="status">Der Beleg wird neu gerendert …</p>
          )}
        </div>
      </div>
    </div>
  );
}
